import {Text} from 'src/design/atoms/Text';
import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo} from 'react';
import type {AssistantUserUsageResponse} from 'src/api/ProactApiClient';
import {
  budgetColor,
  formatPercent,
  formatTokens,
} from 'src/app/assistant/ControlCenter/assistantUsageFormat';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {formatUtcDateTime} from 'src/shared/utils/formatters';

export interface AssistantUserUsageTableProps {
  data: AssistantUserUsageResponse[];
  isLoading: boolean;
  isError: boolean;
  onViewMemory: (user: AssistantUserUsageResponse) => void;
}

export const AssistantUserUsageTable = ({
  data,
  isLoading,
  isError,
  onViewMemory,
}: AssistantUserUsageTableProps) => {
  const columns = useMemo<MRT_ColumnDef<AssistantUserUsageResponse>[]>(
    () => [
      {
        accessorKey: 'userName',
        header: 'User',
        Cell: ({row}) =>
          row.original.userName ?? `User ${row.original.userId}`,
      },
      {
        accessorKey: 'conversationCount',
        header: 'Conversations',
      },
      {
        accessorKey: 'messageCount',
        header: 'Messages',
      },
      {
        id: 'tokens',
        header: 'Tokens',
        accessorFn: row => row.tokens?.totalTokens ?? 0,
        Cell: ({row}) =>
          formatTokens(row.original.tokens?.totalTokens ?? undefined),
      },
      {
        accessorKey: 'lastActivityAtUtc',
        header: 'Last Activity',
        Cell: ({row}) => (
          <Text size="sm" c="dimmed">
            {formatUtcDateTime(row.original.lastActivityAtUtc)}
          </Text>
        ),
      },
      {
        accessorKey: 'todayUserBudgetPercent',
        header: 'Budget Today',
        Cell: ({row}) => (
          <Text size="sm" c={budgetColor(row.original.todayUserBudgetPercent)}>
            {formatPercent(row.original.todayUserBudgetPercent)}
          </Text>
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'assistant-user-usage',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 25,
    },
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      enableColumnFilters: false,
      columns,
      data,
      getRowId: row => String(row.userId),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            tooltip="View memory"
            onClick={() => onViewMemory(row.original)}
          />
        </TableRowActions>
      ),
    },
    totalCount: data.length,
    isError,
    isLoading,
    isFetching: false,
  });

  return table;
};
